import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { DoorOpen, Mail, Send } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const ago = (iso) => {
  const d = Math.floor((Date.now() - new Date(iso).getTime()) / 86400000);
  return d === 0 ? "today" : d === 1 ? "yesterday" : `${d} days ago`;
};

export const CheckoutNudgeCard = ({ onSent }) => {
  const [info, setInfo] = useState(null);
  const [busy, setBusy] = useState(null);
  const [open, setOpen] = useState(false);

  const load = () => axios.get(`${API}/admin/jobs/checkout-nudges`).then((r) => setInfo(r.data)).catch(() => setInfo({ due: [], due_total: 0, sent_total: 0 }));
  useEffect(() => { load(); }, []);

  const run = async (path, label) => {
    setBusy(label);
    try {
      const r = await axios.post(`${API}${path}`);
      const d = r.data;
      if (label === "test") toast[d.email_ok ? "success" : "message"](d.email_ok ? `Test copy sent to ${d.to}.` : `The provider didn't accept it (to ${d.to}) — see the log below.`);
      else toast[d.sent ? "success" : "message"](`Checkout follow-ups: ${d.sent} sent${d.due > d.sent ? ` · ${d.due - d.sent} skipped` : ""}.`);
      load();
      onSent?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || "That didn't go through.");
    } finally {
      setBusy(null);
    }
  };

  const due = info?.due || [];
  return (
    <div className="tacc-card p-5 md:p-6" data-testid="checkout-nudge-card">
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <div className="section-tag mb-2 flex items-center gap-2"><DoorOpen size={12} /> Checkout follow-up</div>
          <div className="font-display text-xl md:text-2xl tracking-tight leading-tight">"Your door's still open."</div>
          <p className="text-[#78716C] text-sm mt-2 max-w-xl">
            One kind email, 24 hours after a signed-in member opened checkout and walked away. Never twice, never to anyone who paid, always with a stop link. Runs daily at 11am ET.
          </p>
          <p className="font-mono text-[10px] tracking-[0.2em] uppercase text-[#78716C] mt-3">
            <span className="text-[#B45309]" data-testid="checkout-nudge-due">{info ? info.due_total : "…"} due now</span>
            {" · "}<span data-testid="checkout-nudge-sent-total">{info ? info.sent_total : "…"} sent so far</span>
            {info?.last_sent_at ? ` · last ${ago(info.last_sent_at)}` : ""}
          </p>
        </div>
        <div className="flex flex-col sm:flex-row gap-2 shrink-0">
          <button onClick={() => run("/admin/jobs/checkout-nudges/test", "test")} disabled={!!busy} className="btn-outline text-xs whitespace-nowrap disabled:opacity-50" data-testid="checkout-nudge-test-btn">
            <Mail size={13} /> {busy === "test" ? "Sending…" : "Send me a test copy"}
          </button>
          <button onClick={() => setOpen((v) => !v)} disabled={!info?.due_total} className="btn-outline text-xs whitespace-nowrap disabled:opacity-40" data-testid="checkout-nudge-review-btn" aria-expanded={open}>
            {open ? "Hide who's due" : `See who's due (${info?.due_total ?? 0})`}
          </button>
        </div>
      </div>
      {open && due.length > 0 && (
        <div className="mt-5 border-t border-[#E7DFD5] pt-4" data-testid="checkout-nudge-list">
          <ul className="space-y-2 text-sm">
            {due.map((d, i) => (
              <li key={d.email} className="flex flex-wrap items-baseline justify-between gap-2 text-[#44403C]" data-testid={`checkout-nudge-row-${i}`}>
                <span>{d.name || "—"} <span className="text-[#78716C] text-xs">{d.email}</span></span>
                <span className="font-mono text-[10px] tracking-[0.15em] uppercase text-[#78716C]">{d.product}{d.amount ? ` · $${Number(d.amount).toFixed(0)}` : ""} · opened {ago(d.started_at)}</span>
              </li>
            ))}
          </ul>
          <button onClick={() => run("/admin/jobs/checkout-nudges", "send")} disabled={!!busy} className="btn-ember text-xs mt-4 disabled:opacity-50" data-testid="checkout-nudge-send-btn">
            <Send size={13} /> {busy === "send" ? "Sending…" : `Send these ${due.length} now (instead of waiting for 11am)`}
          </button>
        </div>
      )}
    </div>
  );
};
